import {
    BarChart3,
    FileBadge,
    FileSearch,
    Gavel,
    Globe2,
    Newspaper,
    Scale,
    ShieldCheck,
    Users,
    type LucideIcon,
} from 'lucide-react';

type Service = {
    title: string;
    description: string;
    icon: LucideIcon;
};

type Category = {
    title: string;
    services: Service[];
};

const categories: Category[] = [
    {
        title: 'Identité légale',
        services: [
            {
                title: 'Annonces FOSC (SHAB)',
                description:
                    'Publications officielles de la Feuille officielle suisse du commerce pour le suivi des évolutions et mutations juridiques.',
                icon: Newspaper,
            },
            {
                title: 'Extraits du Registre du Commerce (RC)',
                description:
                    "Synthèse des données publiques attestant de l'existence juridique et de l'enregistrement d'une société auprès des offices cantonaux.",
                icon: FileBadge,
            },
            {
                title: 'Données IDE / Registre du commerce',
                description:
                    'Informations administratives officielles relatives aux entreprises suisses et aux inscriptions au registre du commerce.',
                icon: FileSearch,
            },
            {
                title: 'Organes & Dirigeants',
                description:
                    'Cartographie des administrateurs, associés, gérants et personnes inscrites avec droit de signature.',
                icon: Users,
            },
        ],
    },
    {
        title: 'Performance financière',
        services: [
            {
                title: 'Informations financières',
                description:
                    'Informations disponibles permettant de mieux comprendre la situation et les caractéristiques financières d’une entreprise.',
                icon: BarChart3,
            },
            {
                title: 'Solvabilité',
                description:
                    'Indicateurs permettant d’évaluer la situation financière et le niveau de risque de vos partenaires commerciaux.',
                icon: Scale,
            },
        ],
    },
    {
        title: 'Conformité & propriété intellectuelle',
        services: [
            {
                title: 'Brevets & Marques',
                description:
                    'Informations relatives à la propriété intellectuelle, aux marques et aux droits enregistrés associés aux entreprises.',
                icon: ShieldCheck,
            },
            {
                title: 'Procédures',
                description:
                    'Informations disponibles concernant les procédures et événements juridiques affectant les entreprises.',
                icon: Gavel,
            },
            {
                title: 'Identification fiscale',
                description:
                    'Informations permettant de vérifier les données fiscales et administratives disponibles concernant une entreprise.',
                icon: Globe2,
            },
        ],
    },
];

export default function ServicesGrid() {
    return (
        <section
            id="catalogue"
            className="bg-[linear-gradient(180deg,var(--color-cream)_0%,var(--color-cream-warm)_100%)] py-20 lg:py-24"
        >
            <div className="container mx-auto max-w-6xl px-4">
                {/* En-tête */}
                <div className="mx-auto mb-14 max-w-2xl text-center">
                    <span className="text-[#b20303] mb-3 inline-block text-xs font-bold tracking-[0.18em] text-accent uppercase">
                        Catalogue
                    </span>

                    <h2 className="mb-3 font-serif text-[clamp(1.875rem,3vw,2.5rem)] leading-tight font-semibold tracking-[-0.015em] text-ink">
                        Les ressources d&apos;entreprise indispensables.
                    </h2>

                    <p className="text-base leading-relaxed text-muted-foreground">
                        Toutes les données légales, financières et juridiques
                        nécessaires à la sécurisation de vos partenariats,
                        issues des informations disponibles sur les entreprises.
                    </p>
                </div>

                {/* Catégories */}
                <div className="flex flex-col gap-12">
                    {categories.map((category) => (
                        <div key={category.title}>
                            {/* Titre catégorie */}
                            <div className="mb-5 flex items-center gap-3">
                                <span
    className="h-px w-12 bg-gradient-to-r from-transparent via-[#b20303] to-[#b20303]"
    aria-hidden="true"
/>
                                <h3 className="text-[11px] font-bold tracking-[0.2em] text-accent uppercase text-[#b20303]">
                                    {category.title}
                                </h3>
                            </div>

                            {/* Services */}
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {category.services.map(
        ({
            title,
            description,
            icon: Icon,
        }) => (
            <article
                key={title}
                className="group relative flex flex-col gap-3 overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-[0_2px_8px_rgba(11,27,43,0.04)] transition-all duration-300 hover:-translate-y-1 hover:border-[#b20303]/40 hover:shadow-[0_12px_30px_rgba(178,3,3,0.12)]"
            >
                {/* Accent line */}
                <div className="absolute left-0 top-0 h-[3px] w-0 bg-[#b20303] transition-all duration-300 group-hover:w-full" />

                {/* Icône */}
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#b20303]/20 bg-[#b20303]/5 text-[#b20303] transition-all duration-300 group-hover:border-[#b20303] group-hover:bg-[#b20303] group-hover:text-white group-hover:shadow-[0_6px_16px_rgba(178,3,3,0.25)]">
                    <Icon
                        className="h-[18px] w-[18px]"
                        strokeWidth={1.8}
                        aria-hidden="true"
                    />
                </div>

                {/* Titre */}
                <h4 className="font-serif text-lg font-bold tracking-tight text-ink transition-colors duration-300 group-hover:text-[#b20303]">
                    {title}
                </h4>

                {/* Description */}
                <p className="text-[14px] leading-relaxed text-muted-foreground">
                    {description}
                </p>
            </article>
        ),
    )}
</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}